import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Accordion, AccordionDetails, AccordionSummary } from '@interstate/components/AccordionGroup';
import type { InterstateTableProps } from '@interstate/components/Table/Types/tableTypes';
import { Table } from '@interstate/components/Table';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { Badge } from '@interstate/components/Badge';
import { InformationCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@interstate/components/Icons';
import { Tooltip } from '@interstate/components/Tooltip';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { AltSoftPullConsentAlert, MandatoryDetailsCard, useMandatoryDetailsSubmit } from '@makemydeal/dr-dash-components';

import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import {
    softpullAltConsentContinueClicked,
    softpullMandatoryDetailsContinueClicked,
    lenderProbabilityDisplayed,
    lenderBackendLimitDetailsClicked
} from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import { getVehicleAnalyticsPayload } from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import type { LenderData } from '@makemydeal/dr-shared-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { useOnceInView } from '../../baseDealActivity/baseDealContainer/hooks/useOnceInView';

const ACCEPTANCE_LEVELS = {
    High: 'High',
    Limited: 'Limited'
} as const;

type AcceptanceLevel = keyof typeof ACCEPTANCE_LEVELS;

interface LenderProbabilitiesProps {
    showProfit: boolean;
    lenderData: LenderData[] | false;
    isInsideSlideOut?: boolean;
}

const ACCEPTANCE_TOOLTIPS: Record<string, string> = {
    High: 'Based on historical data patterns, this lender has higher approval likelihood for comparable deals.',
    Limited: 'Based on historical data patterns, this lender has lower approval likelihood for comparable deals.'
};

// Matches a plain monetary amount such as "$2,800.00", "$3,400", or "2800.00".
const SINGLE_VALUE_BACKEND_LIMIT_PATTERN = /^\$?\s?[\d,]+(\.\d{1,2})?$/;

// A backend limit is a "single value" (e.g. "$2,800.00") when it collapses to exactly one
// non-blank line that is a plain monetary amount. These are shown inline; multi-line free-form
// limits keep the expandable "View details" treatment.
const isSingleValueBackendLimit = (backendLimit: string): boolean => {
    const lines = backendLimit
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    return lines.length === 1 && SINGLE_VALUE_BACKEND_LIMIT_PATTERN.test(lines[0]);
};

const LenderProbabilities = ({ showProfit, lenderData, isInsideSlideOut = false }: LenderProbabilitiesProps) => {
    const dispatch = useDispatch();
    const theme = useInterstateTheme();

    // Soft pull consent selectors
    const softPullConsentProvided = useSelector(offerInfoSelectors.getSoftPullConsentProvided);

    const hasIdentityDataCompleteness = useSelector(customerSpdSelectors.getHasIdentityDataCompleteness);
    const isEnableSoftPullConsentMV = useSelector(featureToggleSelectors.isEnableSoftPullConsentMV);
    const isEnableSoftPullConsentWithoutDLMV = useSelector(featureToggleSelectors.isEnableSoftPullConsentWithoutDLMV);
    const isSpdManagementEnabled = useSelector(dealerSelectors.isSpdManagementEnabled);
    const isLenderComparisonWithProfitEnabled = useSelector(featureToggleSelectors.isLenderComparisonWithProfitEnabled);

    // Vehicle analytics payload for soft pull consent tags
    const vehicleAnalyticsPayload = useSelector(getVehicleAnalyticsPayload);

    // Compute whether to show consent alert (DL or First Name, Last Name, Zip, DOB when toggle enabled)
    const shouldShowConsentAlert = isEnableSoftPullConsentMV && softPullConsentProvided === false && hasIdentityDataCompleteness;
    // State 1 (Mandatory Details) is the "soft-pull without DL" path — gate on the no-DL toggle so
    // we preserve the existing master-switch semantic: toggle OFF + no DL = no soft-pull UI at all.
    // softPullConsentProvided check uses !== true (not === false) because the modal that flips it
    // to false only opens when identity is complete; State 1 fires on the inverse, so the value
    // stays undefined on fresh deals.
    const shouldShowMandatoryDetails =
        isEnableSoftPullConsentMV &&
        softPullConsentProvided !== true &&
        !hasIdentityDataCompleteness &&
        isSpdManagementEnabled &&
        isEnableSoftPullConsentWithoutDLMV;

    const closeOrNavigateToSummary = () => {
        if (isInsideSlideOut) {
            dispatch(baseDealActionCreators.closeSlideOut());
        } else {
            dispatch(baseDealActionCreators.handleTabChange(DealTabs.SUMMARY));
        }
    };

    const handleConsentAccept = () => {
        dispatch(softpullAltConsentContinueClicked(vehicleAnalyticsPayload)); // dc-mv-281: Analytics tag
        dispatch(offerActionCreators.setSoftPullConsentProvided(true));
        closeOrNavigateToSummary();
    };

    const {
        initialValues: mandatoryDetailsInitialValues,
        onSubmit: handleMandatoryDetailsSubmit,
        isSubmitting: isMandatoryDetailsSubmitting
    } = useMandatoryDetailsSubmit({
        onComplete: closeOrNavigateToSummary
    });

    // Fire analytics for each lender/likelihood pair when the component first comes into view (dc-mv-278)
    // Empty deps: intentionally capture initial values only - AI refresh may update lenderData and vehicle data later
    const inViewRef = useOnceInView(
        useCallback(() => {
            if (lenderData && lenderData.length > 0) {
                lenderData.forEach((lender) => {
                    dispatch(
                        lenderProbabilityDisplayed(
                            {
                                lenderId: lender.lenderId ?? lender.lender,
                                lenderName: lender.lender,
                                loanLikelihood: lender.approveProb,
                                buyRate: lender.buyRate,
                                // ILS-powered backend limit (dc-mv-278).
                                backendLimit: lender.backendLimit
                            },
                            vehicleAnalyticsPayload
                        )
                    );
                });
            }
        }, [])
    );

    // Expandable row state for backend limit details — multiple rows can be expanded
    const [expandedLenders, setExpandedLenders] = useState<Set<string>>(new Set());

    // Don't render if there's no data
    if (!lenderData || lenderData.length === 0) {
        return null;
    }

    const enrichedLenderData = isLenderComparisonWithProfitEnabled
        ? lenderData.map((lender) => ({
              ...lender,
              key: lender.lender,
              backendLimit: lender.backendLimit
          }))
        : lenderData;

    // Toggles the expanded row and tags the expand direction only — collapsing isn't a "view details"
    // intent. `key` is a rendered lender name (rowKey="lender") and rowExpandable only allows rows with
    // a multi-line backendLimit, so the lookup always resolves.
    const handleExpandClick = ({ key, expanded }: { key: string; expanded: boolean }) => {
        setExpandedLenders((prev) => {
            const next = new Set(prev);
            if (expanded) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });

        if (!expanded) return;
        // dc-mv-321: Analytics tag
        const lender = lenderData.find((item) => item.lender === key)!;
        dispatch(
            lenderBackendLimitDetailsClicked(
                {
                    lenderId: lender.lenderId ?? lender.lender,
                    lenderName: lender.lender,
                    loanLikelihood: lender.approveProb,
                    buyRate: lender.buyRate,
                    backendLimit: lender.backendLimit
                },
                vehicleAnalyticsPayload
            )
        );
    };

    const cellVariant = isLenderComparisonWithProfitEnabled ? 'body-sm' : 'body-md';

    const columns: InterstateTableProps['columns'] = [
        {
            title: 'Lender',
            dataIndex: 'lender',
            key: 'lender',
            ...(isLenderComparisonWithProfitEnabled && { width: '30%' }),
            render: (text: string) => (
                <Typography variant={cellVariant} tag="span">
                    {text}
                </Typography>
            )
        },
        {
            title: <span style={{ whiteSpace: 'nowrap' }}>Acceptance Likelihood</span>,
            dataIndex: 'loanLikelihood',
            align: isLenderComparisonWithProfitEnabled ? 'left' : 'center',
            key: 'loanLikelihood',
            ...(isLenderComparisonWithProfitEnabled && { width: '30%' }),
            render: (loanLikelihood: string) => {
                const level = loanLikelihood as AcceptanceLevel;
                const tooltip = ACCEPTANCE_TOOLTIPS[level];
                return (
                    <Box display="inline-flex" alignItems="center" gap={1}>
                        <Box sx={{ minWidth: '80px', display: 'inline-flex', justifyContent: 'center' }}>
                            <Badge
                                id="ai-supported-badge"
                                variant={level === ACCEPTANCE_LEVELS.High ? 'success' : 'light'}
                                className="statusContainer"
                            >
                                <Typography variant={cellVariant} tag="span">
                                    {level}
                                </Typography>
                            </Badge>
                        </Box>
                        {tooltip && (
                            <Tooltip size="large" position="bottom" toolTipContent={tooltip}>
                                <InformationCircleIcon
                                    data-testid="acceptance-likelihood-info-icon"
                                    color={theme.tokens.BaseColorBlue500}
                                />
                            </Tooltip>
                        )}
                    </Box>
                );
            }
        }
    ];

    if (showProfit) {
        columns.push({
            title: isLenderComparisonWithProfitEnabled ? 'Profit Potential' : 'Estimated Profit Potential',
            dataIndex: 'totalProfit',
            align: isLenderComparisonWithProfitEnabled ? 'left' : 'right',
            key: 'totalProfit',
            ...(isLenderComparisonWithProfitEnabled && { width: '20%' }),
            render: (totalProfit: number | undefined) => {
                return (
                    <Typography variant={cellVariant} tag="span">
                        {totalProfit ? `$${formatNumberAndCents(totalProfit)}` : '-'}
                    </Typography>
                );
            }
        });
    }

    if (isLenderComparisonWithProfitEnabled) {
        columns.push({
            title: 'Backend Limit',
            dataIndex: 'backendLimit',
            align: 'left',
            key: 'backendLimit',
            width: '20%',
            render: (backendLimit: string | null | undefined, record: LenderData) => {
                const hasDetails = !!backendLimit;
                if (!hasDetails) {
                    return (
                        <Typography variant="body-sm" tag="span">
                            N/A
                        </Typography>
                    );
                }
                // A single monetary value is shown inline instead of an expandable "View details" link.
                if (isSingleValueBackendLimit(backendLimit)) {
                    return (
                        <Typography variant={cellVariant} tag="span">
                            {backendLimit}
                        </Typography>
                    );
                }
                const isExpanded = expandedLenders.has(record.lender);
                return (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Typography variant="body-xs" tag="span" color="base.color.blue.700" sx={{ fontSize: '12px' }}>
                            View details
                        </Typography>
                        {isExpanded ? <ChevronDownIcon fontSize="12px" /> : <ChevronRightIcon fontSize="12px" />}
                    </Box>
                );
            }
        });
    }

    return (
        <Box paddingBottom={3} ref={inViewRef}>
            {shouldShowMandatoryDetails && (
                <MandatoryDetailsCard
                    initialValues={mandatoryDetailsInitialValues}
                    onSubmit={(values) => {
                        dispatch(softpullMandatoryDetailsContinueClicked(vehicleAnalyticsPayload)); // dc-mv-300: Analytics tag
                        handleMandatoryDetailsSubmit(values);
                    }}
                    isSubmitting={isMandatoryDetailsSubmitting}
                />
            )}
            {shouldShowConsentAlert && <AltSoftPullConsentAlert onAccept={handleConsentAccept} />}
            <Accordion
                defaultExpanded={true}
                data-testid="lender-probabilities-accordion"
                sx={{ '& .MuiAccordion-root': { borderBottom: 'none' }, borderBottom: 'none' }}
            >
                <AccordionSummary chevronPosition="left" data-testid="lender-probabilities-summary">
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="h4" color="base.color.blue.700" data-testid="lender-comparison-title">
                            Lender Comparison
                        </Typography>
                        <Badge id="ai-supported-badge" variant="transparent" className="statusContainer">
                            <AiLogoIcon data-testid="ai-lender-supported-icon" fontSize="14px" ariaHidden={true} />
                            <span>AI Supported</span>
                        </Badge>
                    </Box>
                </AccordionSummary>
                <AccordionDetails data-testid="lender-probabilities-details">
                    <Box
                        sx={
                            isLenderComparisonWithProfitEnabled
                                ? {
                                      '& colgroup > col:first-of-type': {
                                          width: '0 !important'
                                      },
                                      '& .ant-table-row-expand-icon-cell': {
                                          width: '0 !important',
                                          maxWidth: '0 !important',
                                          minWidth: '0 !important',
                                          padding: '0 !important',
                                          border: 'none !important',
                                          overflow: 'hidden',
                                          '& > *': { display: 'none' }
                                      },
                                      '& .ant-table-thead > tr > th:nth-of-type(2), & .ant-table-tbody > tr > td:nth-of-type(2)': {
                                          paddingLeft: '0 !important'
                                      },
                                      // Remove the expanded row cell's default padding (antd applies
                                      // `padding: 16px ...` via `td[colspan]:not([colspan="1"])`) so its content aligns
                                      // flush with the Lender column text (e.g. "Ally"). Match antd's selector shape so
                                      // this override wins on specificity regardless of stylesheet order.
                                      '& .ant-table-tbody > tr.ant-table-expanded-row > td[colspan]:not([colspan="1"])': {
                                          padding: '0 !important'
                                      }
                                  }
                                : undefined
                        }
                    >
                        <Table
                            columns={columns}
                            data={enrichedLenderData}
                            enablePagination={false}
                            dataDensity={isLenderComparisonWithProfitEnabled ? 'small' : 'large'}
                            rowKey="lender"
                            data-testid="lender-probabilities-table"
                            {...(isLenderComparisonWithProfitEnabled && {
                                expandRowOnClick: true,
                                expandedRows: Array.from(expandedLenders),
                                onExpandClick: handleExpandClick,
                                rowExpandable: (record: LenderData) =>
                                    !!record.backendLimit && !isSingleValueBackendLimit(record.backendLimit),
                                expandedRowComponent: (record: LenderData) => {
                                    // rowExpandable guarantees backendLimit is defined. backendLimit is free-form text
                                    // that varies per lender, so render it faithfully: split on newlines, drop blank
                                    // lines, and show every line as a bullet point (stripping any leading bullet
                                    // character the source already includes) so no content is dropped.
                                    // Always show a heading: if the data's first line is already "ALLOWABLE BACKEND",
                                    // use it as the heading; otherwise add a default "Allowed backend limit" heading.
                                    const rawLines = record
                                        .backendLimit!.split('\n')
                                        .map((l) => l.trim())
                                        .filter(Boolean);
                                    const hasOwnHeading = rawLines[0]?.toUpperCase() === 'ALLOWABLE BACKEND';
                                    const heading = hasOwnHeading ? rawLines[0] : 'Allowed backend limit';
                                    const bulletItems = (hasOwnHeading ? rawLines.slice(1) : rawLines).map((l) =>
                                        l.replace(/^[•▪]\s*/, '')
                                    );
                                    return (
                                        <Box
                                            sx={{ backgroundColor: '#f5f5f5', padding: '12px 16px' }}
                                            data-testid={`backend-limit-details-${record.lender}`}
                                        >
                                            <Typography variant="body-sm" tag="p" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                {heading}
                                            </Typography>
                                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                {bulletItems.map((line, idx) => (
                                                    <li key={idx} style={{ lineHeight: 1.4 }}>
                                                        <Typography variant="body-sm" tag="span">
                                                            {line}
                                                        </Typography>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    );
                                }
                            })}
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default LenderProbabilities;
